import AddIcon from '@mui/icons-material/Add';
import { Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LuPencil } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import {
  AutomatedEmailCampaignType,
  IAutomatedEmailCampaignSettingListPayload,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  FieldOption,
  getDateUsingFormat,
  WsButton,
  WsDataGrid,
  WsListingLoader,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import SearchBar from '../../../../features/teams/components/SearchBarWrapper';
import { ChipIconVariants, StatusIconChip } from '../../components';
import { useGetSettinsCampaignList } from '../../hooks';
import {
  defaultAutomatedEmailCampaignSettingListPayload,
  getCampaignSettingsColumns,
} from '../../utils';

export const JobCampaignList = () => {
  const methods = useForm();
  const navigate = useNavigate();
  const [search, setSearch] = useState<IAutomatedEmailCampaignSettingListPayload>(
    defaultAutomatedEmailCampaignSettingListPayload(AutomatedEmailCampaignType.JOB)
  );
  const { campaignSettingList, loading } = useGetSettinsCampaignList(search);
  const [settings, setSettings] = useState<FieldOption[]>(
    getCampaignSettingsColumns(AutomatedEmailCampaignType.JOB)
  );
  const onColumnSettingsReset = () =>
    setSettings(getCampaignSettingsColumns(AutomatedEmailCampaignType.JOB));
  const handlePageChange = (newPage: number) => setSearch({ ...search, page: newPage });

  const handlePageSizeChange = (newPageSize: number) => {
    setSearch({ ...search, size: newPageSize, page: 0 });
  };
  const handleNavigation = (params) =>
    navigate(
      `/automated-email-campaign-settings/add-new-campaign?type=${AutomatedEmailCampaignType.JOB}&id=${params.value}`
    );
  const handleAddNavigation = () =>
    navigate(
      `/automated-email-campaign-settings/add-new-campaign?type=${AutomatedEmailCampaignType.JOB}`
    );
  const data = useMemo(
    () =>
      campaignSettingList?.content?.map((campaignSetting) => ({
        id: campaignSetting.jobId,
        title: campaignSetting.metaDetails.title,
        jobCode: campaignSetting.metaDetails.jobCode,
        createdOn: getDateUsingFormat(campaignSetting.createdOn.toString(), 'DD MMM, YYYY'),
        status: campaignSetting.emailCheck,
      })) ?? [],
    [campaignSettingList]
  );
  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Job Name',
      sortable: false,
      flex: 1,
      renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
    },

    {
      field: 'jobCode',
      headerName: 'Job Code',
      sortable: false,
      flex: 1,
      renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
    },

    {
      field: 'createdOn',
      headerName: 'Date Created',
      sortable: false,
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <StatusIconChip
          label={params.value ? 'Active' : 'Inactive'}
          variant={params.value ? ChipIconVariants.ACTIVE : ChipIconVariants.INACTIVE}
        />
      ),
    },
    {
      field: 'id',
      headerName: '',
      sortable: false,
      flex: 0.3,
      renderCell: (params) => (
        <LuPencil cursor="pointer" onClick={() => handleNavigation(params)} />
      ),
    },
  ];

  return (
    <Grid container overflow="hidden" rowGap={2}>
      <Grid container item xs={12} justifyContent="space-between" alignItems="center">
        <Grid item>
          <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
            Job
          </WsTypography>
        </Grid>
        <Grid item>
          <WsButton startIcon={<AddIcon />} variant="contained" onClick={handleAddNavigation}>
            Add Campaign
          </WsButton>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <SearchBar name="query" />
        </FormProvider>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <WsListingLoader minHeight={400} />
        ) : (
          <>
            {campaignSettingList.totalElements ? (
              <WsDataGrid
                autoHeight
                rows={data}
                columns={columns}
                paginationMode="server"
                page={search.page ?? 0}
                onPageChange={handlePageChange}
                pageSize={search.size ?? 20}
                onPageSizeChange={handlePageSizeChange}
                totalRows={campaignSettingList.totalElements}
                sortingMode="server"
                columnVisibility
                columnSettings={settings}
                onColumnSettingsChange={(data) => setSettings(data)}
                onColumnSettingsReset={onColumnSettingsReset}
              />
            ) : (
              <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};
